import { formatDate } from '../../../../../shared/sso-workspace/format'

// Why a removed member was removed, as the organization reads it (spec
// 2026-10-23-scim-provisioning-design R25, owner decision 2026-10-23: "it
// should show/state why"). Content's recycle-bin rows (GET
// /api/ddd/user-login/deleted and /:id/deleted) carry removedVia (admin,
// staff, scim, partner_api or self), removedBy (a person's name,
// "EducationXR support", "<key name> API key", or null for SCIM),
// removedAt and sticky (SSO sign-in won't add them back until an admin
// restores them). A removal from before R25 carries none: nothing is said.

export const STICKY_TEXT = "SSO sign-in won't add them back until you restore them."

// An identity provider in words (owner 2026-10-29): its product and the
// organization's name for the connection, "Microsoft Entra ID (EdXR Azure
// Dev)", either alone when that is all content knows, or '' for neither.
// `conn` is content's removedByConnection or managedBy ({ name |
// connectionName, displayName, vendor }).
export const providerName = conn => {
  if (!conn) return ''
  const label = (conn.displayName || '').trim() || (conn.name || conn.connectionName || '').trim()
  const vendor = (conn.vendor || '').trim()
  if (vendor && label && vendor.toLowerCase() !== label.toLowerCase()) return `${vendor} (${label})`
  return vendor || label
}

// The connection behind a SCIM removal: the one whose provisioning token
// removed them (content's removedByConnection), else the one that
// provisions them (managedBy kind scim); null otherwise.
export const removalConnection = row => {
  if (!row || row.removedVia !== 'scim') return null
  if (row.removedByConnection) return row.removedByConnection
  return row.managedBy && row.managedBy.kind === 'scim' ? row.managedBy : null
}

// Who removed them, without the date: "Kim Lee", "EducationXR support",
// "Microsoft Entra ID (EdXR Azure Dev)", "your identity provider", or ''.
export const removedByText = row => {
  if (!row || !row.removedVia) return ''
  switch (row.removedVia) {
    case 'self': return ''
    case 'scim': return providerName(removalConnection(row)) || 'your identity provider'
    default: return row.removedBy || ''
  }
}

// "Removed by Kim Lee on Oct 23, 2026." / "Removed by your identity
// provider on ..." / "Left the organization on ...", or '' when unknown.
export const removedText = row => {
  if (!row || !row.removedVia) return ''
  const on = row.removedAt ? ` on ${formatDate(row.removedAt)}` : ''
  switch (row.removedVia) {
    case 'self': return `Left the organization${on}.`
    default: {
      const by = removedByText(row)
      return by ? `Removed by ${by}${on}.` : `Removed${on}.`
    }
  }
}

// Both sentences: { text, sticky } (sticky: STICKY_TEXT or null), or null
// when the row says nothing about its removal.
export const removalOf = row => {
  const text = removedText(row)
  if (!text) return null
  return { text, sticky: row.sticky ? STICKY_TEXT : null }
}

// One line, for an export or a hover.
export const removalLine = row => {
  const removal = removalOf(row)
  return removal ? [removal.text, removal.sticky].filter(Boolean).join(' ') : ''
}
